/**
 * Transition Manager - Manages gradual migration from client-side to cloud functions
 * 
 * Provides environment switching, feature flags, and debugging capabilities
 * during the transition period.
 */

import { disciplineLogger } from '../../../utils/disciplineLogger';

export class TransitionManager {
  constructor() {
    // Feature flags for gradual migration
    this.flags = {
      useCloudSequentialCounters: true,          // Phase 1: Move sequential counting
      useCloudRuleEvaluation: false,             // Phase 2: Move rule evaluation
      enableVerboseLogging: true,                 // Debug mode
      enableComparisonMode: false,                 // Compare client vs cloud results
      enableFallbackOnError: true                  // Fallback to client-side on errors
    };

    // Load flags from localStorage if available
    this.loadFlags();
  }

  /**
   * Load feature flags from localStorage
   */
  loadFlags() {
    try {
      const stored = localStorage.getItem('swanTransitionFlags');
      if (stored) {
        this.flags = { ...this.flags, ...JSON.parse(stored) };
      }
    } catch (error) {
      console.warn('Failed to load transition flags:', error);
    }
  }

  /**
   * Save feature flags to localStorage
   */
  saveFlags() {
    try {
      localStorage.setItem('swanTransitionFlags', JSON.stringify(this.flags));
    } catch (error) {
      console.warn('Failed to save transition flags:', error);
    }
  }

  /**
   * Set a feature flag
   */
  setFlag(flagName, value) {
    this.flags[flagName] = value;
    this.saveFlags();
    
    disciplineLogger.info(`🚩 Transition flag changed: ${flagName} = ${value}`, {
      flag: flagName,
      value,
      allFlags: this.flags
    }, 'transition');
  }

  /**
   * Get a feature flag value
   */
  getFlag(flagName) {
    return this.flags[flagName];
  }

  /**
   * Log comparison between client and cloud results
   */
  logComparison(operation, clientResult, cloudResult, context = {}) {
    if (!this.flags.enableComparisonMode) return;

    const isMatch = this.deepEqual(clientResult, cloudResult);
    
    disciplineLogger.info(`🔍 Comparison: ${operation}`, {
      operation,
      isMatch,
      clientResult,
      cloudResult,
      context,
      timestamp: new Date().toISOString()
    }, 'transition');

    if (!isMatch) {
      console.warn(`⚠️ Client/Cloud mismatch for ${operation}:`, {
        client: clientResult,
        cloud: cloudResult,
        context
      });
    }
  }

  /**
   * Log transition step with debugging info
   */
  logStep(step, data, category = 'transition') {
    if (!this.flags.enableVerboseLogging) return;

    disciplineLogger.info(`🔄 Transition Step: ${step}`, {
      step,
      data,
      flags: this.flags,
      timestamp: new Date().toISOString()
    }, category);

    // Also log to console for immediate debugging
    console.log(`🔄 Transition [${step}]:`, data);
  }

  /**
   * Execute operation with fallback to client-side
   */
  async executeWithFallback(cloudOperation, fallbackOperation, context = {}) {
    if (!this.flags.useCloudRuleEvaluation && !this.flags.useCloudSequentialCounters) {
      this.logStep('Using client-side (cloud disabled)', context);
      return await fallbackOperation();
    }

    try {
      this.logStep('Attempting cloud operation', context);
      const cloudResult = await cloudOperation();

      if (this.flags.enableComparisonMode) {
        const clientResult = await fallbackOperation();
        this.logComparison(context.operation, clientResult, cloudResult, context);
      }

      this.logStep('Cloud operation successful', { result: cloudResult });
      return cloudResult;

    } catch (error) {
      if (this.flags.enableFallbackOnError) {
        this.logStep('Cloud operation failed, falling back to client-side', { 
          error: error.message, 
          context 
        }, 'transition-error');

        console.warn('⚠️ Cloud operation failed, using fallback:', error);
        return await fallbackOperation();
      } else {
        this.logStep('Cloud operation failed (no fallback)', { 
          error: error.message, 
          context 
        }, 'transition-error');
        throw error;
      }
    }
  }

  /**
   * Deep equality check for comparison mode
   */
  deepEqual(obj1, obj2) {
    try {
      return JSON.stringify(obj1) === JSON.stringify(obj2);
    } catch (error) {
      return false;
    }
  }

  /**
   * Get current transition status for UI
   */
  getStatus() {
    return {
      phase: this.determinePhase(),
      flags: this.flags,
      recommendations: this.getRecommendations()
    };
  }

  /**
   * Determine current migration phase
   */
  determinePhase() {
    if (!this.flags.useCloudSequentialCounters && !this.flags.useCloudRuleEvaluation) {
      return 'Phase 0: Client-side only';
    }
    if (this.flags.useCloudSequentialCounters && !this.flags.useCloudRuleEvaluation) {
      return 'Phase 1: Sequential counters migrated';
    }
    if (this.flags.useCloudSequentialCounters && this.flags.useCloudRuleEvaluation) {
      return 'Phase 2: Full migration complete';
    }
    return 'Unknown phase';
  }

  /**
   * Get recommendations based on current state
   */
  getRecommendations() {
    const recommendations = [];

    if (!this.flags.useCloudSequentialCounters) {
      recommendations.push({
        priority: 'high',
        action: 'Enable cloud sequential counters',
        reason: 'Fixes data integrity issues with sequential numbering'
      });
    }

    if (this.flags.useCloudSequentialCounters && !this.flags.useCloudRuleEvaluation) {
      recommendations.push({
        priority: 'medium',
        action: 'Enable cloud rule evaluation',
        reason: 'Moves business logic to server-side for consistency'
      });
    }

    if (!this.flags.enableComparisonMode && this.flags.useCloudRuleEvaluation) {
      recommendations.push({
        priority: 'low',
        action: 'Enable comparison mode temporarily',
        reason: 'Validate cloud function results match client-side'
      });
    }

    return recommendations;
  }

  /**
   * Enable all cloud features (for final migration)
   */
  enableCloudFeatures() {
    this.flags.useCloudSequentialCounters = true;
    this.flags.useCloudRuleEvaluation = true;
    this.flags.enableComparisonMode = false;
    this.flags.enableFallbackOnError = false;
    this.saveFlags();
    
    this.logStep('All cloud features enabled', { flags: this.flags });
  }

  /**
   * Disable all cloud features (rollback)
   */
  disableCloudFeatures() {
    this.flags.useCloudSequentialCounters = false;
    this.flags.useCloudRuleEvaluation = false;
    this.flags.enableComparisonMode = false;
    this.flags.enableFallbackOnError = true;
    this.saveFlags();
    
    this.logStep('All cloud features disabled (rollback)', { flags: this.flags });
  }
}

// Create singleton instance
export const transitionManager = new TransitionManager();

// Export for global access (browser console debugging)
if (typeof window !== 'undefined') {
  window.swanTransition = transitionManager;
  
  // Convenience methods for browser console
  window.enableCloudCounters = () => transitionManager.setFlag('useCloudSequentialCounters', true);
  window.enableCloudRules = () => transitionManager.setFlag('useCloudRuleEvaluation', true);
  window.enableComparison = () => transitionManager.setFlag('enableComparisonMode', true);
  window.disableCloudFeatures = () => transitionManager.disableCloudFeatures();
  window.enableCloudFeatures = () => transitionManager.enableCloudFeatures();
  window.transitionStatus = () => console.table(transitionManager.getStatus());
}